import { inject, provide } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';

const SidebarControl = Symbol('SidebarControl');

export function useSidebarContext() {
  const context = inject(SidebarControl, null);
  if (context === null) {
    throw new Error(`Component is missing a parent <Sidebar /> component.`);
  }

  const router = useRouter();
  const { shouldShow } = usePolicy();

  // router.resolve joga exception quando a rota não existe (ex: feature
  // listada no menu mas rota não registrada ainda). Encapsula em try/catch
  // pra não quebrar a renderização da sidebar inteira.
  const safeResolve = to => {
    if (!to) return null;
    try {
      return router.resolve(to);
    } catch {
      return null;
    }
  };

  const resolvePath = to => safeResolve(to)?.path || '/';

  const resolvePermissions = to => safeResolve(to)?.meta?.permissions ?? [];

  const resolveFeatureFlag = to => safeResolve(to)?.meta?.featureFlag || '';

  const resolveInstallationType = to =>
    safeResolve(to)?.meta?.installationTypes || [];

  const isAllowed = to => {
    // Rota não registrada (ex: feature listada no menuItems mas removida)
    // — esconde o item pra não estourar o RouterLink.
    if (!safeResolve(to)) return false;

    const permissions = resolvePermissions(to);
    const featureFlag = resolveFeatureFlag(to);
    const installationType = resolveInstallationType(to);

    return shouldShow(featureFlag, permissions, installationType);
  };

  return {
    ...context,
    resolvePath,
    resolvePermissions,
    resolveFeatureFlag,
    isAllowed,
  };
}

export function provideSidebarContext(context) {
  provide(SidebarControl, context);
}
